import { FormSelect } from '@/features/missions/components/FormSelect'
import {
  TREMENDOUS_NOT_AVAILABLE,
  type TremendousOptions,
  type TremendousProduct,
  tremendousOptionsErrorCode
} from '@/features/wallet/api'
import { productsForCountry, useCountryOptions } from '@/features/wallet/utils/countries'
import { type FormLimitsCents, describeAmounts, formLimitsToCents } from '@/features/wallet/utils/tremendousBands'
import { Button } from '@/ui-kit'
import { Trans, useLingui } from '@lingui/react/macro'
import type { UseQueryResult } from '@tanstack/react-query'
import { useState } from 'react'

type TremendousOptionsBrowserProps = {
  /** The link's one observer; a second one here would refetch a failed query on open. */
  query: UseQueryResult<TremendousOptions>
  minPayout: number
  maxAmountUsd: number | undefined
}

type ViewState = 'not-available' | 'list' | 'loading' | 'unavailable'

// NOT_AVAILABLE hides even a cached list; any other error keeps a cached list; with no list,
// a pending query is loading (including LOADING retries) and an errored one is unavailable.
const viewState = (query: UseQueryResult<TremendousOptions>): ViewState => {
  if (tremendousOptionsErrorCode(query.error) === TREMENDOUS_NOT_AVAILABLE) return 'not-available'
  if (query.data !== undefined) return 'list'
  if (query.status === 'pending') return 'loading'
  return 'unavailable'
}

const ProductRow = ({
  product,
  limits,
  locale
}: {
  product: TremendousProduct
  limits: FormLimitsCents
  locale: string
}) => {
  const [failed, setFailed] = useState(false)
  const { text, fixedOnly } = describeAmounts(product, limits, locale)
  return (
    <li className="flex items-center gap-3 py-2">
      {product.logoUrl !== null && !failed && (
        <img
          src={product.logoUrl}
          alt=""
          className="size-10 shrink-0 rounded-lg object-contain"
          onError={() => setFailed(true)}
        />
      )}
      <div className="flex min-w-0 flex-col">
        <span data-slot="product-name" className="text-body-2 font-medium">
          {product.name}
        </span>
        <span className="text-body-3 text-gray-80">{text}</span>
        {fixedOnly && (
          <span className="text-body-3 text-gray-80">
            <Trans>Fixed amounts only</Trans>
          </span>
        )}
      </div>
    </li>
  )
}

export const TremendousOptionsBrowser = ({ query, minPayout, maxAmountUsd }: TremendousOptionsBrowserProps) => {
  const { t, i18n } = useLingui()
  const countryOptions = useCountryOptions()
  const [country, setCountry] = useState<string | undefined>(undefined)
  const state = viewState(query)
  const limits = formLimitsToCents(minPayout, maxAmountUsd)
  const products = state === 'list' && country !== undefined ? productsForCountry(query.data!.products, country) : []

  return (
    <div className="flex flex-col gap-4">
      <FormSelect
        searchable
        value={country}
        onChange={setCountry}
        label={t`Country`}
        options={countryOptions}
        placeholder={t`Choose a country`}
      />

      {state === 'not-available' && (
        <div className="text-body-2 text-gray-80">{t`Tremendous isn't available right now`}</div>
      )}

      {state === 'unavailable' && (
        <div className="flex flex-col items-start gap-3">
          <div className="text-body-2 text-gray-80">{t`Couldn't load options — try again.`}</div>
          <Button variant="bordered-gray" size="small" onClick={() => void query.refetch()}>
            <Trans>Try again</Trans>
          </Button>
        </div>
      )}

      {state === 'loading' && (
        <div className="text-body-2 text-gray-80">
          <Trans>Loading options…</Trans>
        </div>
      )}

      {state === 'list' && country !== undefined && (
        <div className="flex max-h-[50vh] flex-col gap-3 overflow-y-auto">
          {products.length > 0 ? (
            <ul className="flex flex-col">
              {products.map((p) => (
                <ProductRow key={p.id} product={p} limits={limits} locale={i18n.locale} />
              ))}
            </ul>
          ) : (
            <div className="text-body-2 text-gray-80">
              <Trans>Tremendous lists no options for this country.</Trans>
            </div>
          )}
          <div className="text-body-3 text-gray-80">
            <Trans>
              These are the options Tremendous lists for this country. The redemption page shows options for the country
              you open your link from, so they may differ.
            </Trans>
          </div>
        </div>
      )}
    </div>
  )
}
